import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { QuestionView, type PublicQuestion } from './QuestionView';
import { CountdownTimer } from './CountdownTimer';
import { ProgressBar } from './ProgressBar';
import { HelpPanel } from './HelpPanel';

export interface TimedAttemptData {
  attemptId: string;
  phase: 'benchmark' | 'dojo';
  deadlineAt: string;
  status: 'in_progress' | 'submitted' | 'expired';
  questions: PublicQuestion[];
  answers: { questionId: string; selected: string[] }[];
}

interface AnswerState {
  selected: string[];
  result?: { correct: boolean; correctAnswerIds: string[] };
}

export function TimedAttempt({ initial }: { initial: TimedAttemptData }) {
  const navigate = useNavigate();
  const [data] = useState<TimedAttemptData>(initial);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>(() => {
    const seed: Record<string, AnswerState> = {};
    for (const a of initial.answers) seed[a.questionId] = { selected: a.selected };
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const q = data.questions[idx];
  const current = q ? answers[q.id] : undefined;
  const total = data.questions.length;
  const answered = Object.values(answers).filter((a) => a.selected.length > 0).length;
  const correctCount = useMemo(
    () => Object.values(answers).filter((a) => a.result?.correct).length,
    [answers]
  );

  const setForCurrent = useCallback(
    (selected: string[]) => {
      if (!q) return;
      setAnswers((prev) => ({
        ...prev,
        [q.id]: { selected, result: prev[q.id]?.result }
      }));
    },
    [q]
  );

  const submitAnswer = useCallback(async () => {
    if (!q) return;
    const sel = answers[q.id]?.selected ?? [];
    if (sel.length === 0) return;
    try {
      const r = await api.post<{ correct: boolean; correctAnswerIds: string[] }>(
        `/api/take/attempt/${data.attemptId}/answer`,
        { questionId: q.id, selected: sel }
      );
      setAnswers((prev) => ({
        ...prev,
        [q.id]: {
          selected: sel,
          result: { correct: r.correct, correctAnswerIds: r.correctAnswerIds }
        }
      }));
    } catch {
      // ignore — UI stays in unsubmitted state
    }
  }, [q, answers, data.attemptId]);

  const finishTest = useCallback(async () => {
    setSubmitting(true);
    try {
      await api.post(`/api/take/attempt/${data.attemptId}/submit`);
      navigate(`/results/${data.attemptId}`);
    } finally {
      setSubmitting(false);
    }
  }, [data, navigate]);

  if (data.status === 'submitted') {
    navigate(`/results/${data.attemptId}`);
    return null;
  }
  if (!q) return <p className="loading">No questions.</p>;

  const result = current?.result;
  const isLast = idx === total - 1;

  return (
    <div className="page-attempt">
      <header className="attempt-bar">
        <ProgressBar
          value={answered}
          max={total}
          label={`Answered ${answered}/${total} · ✓ ${correctCount} correct`}
        />
        <div className="attempt-bar-right">
          <button type="button" className="btn btn-link" onClick={() => setShowHelp(true)}>
            ❓ Help
          </button>
          <CountdownTimer deadlineAt={data.deadlineAt} onExpire={finishTest} />
        </div>
      </header>

      <QuestionView
        question={q}
        selected={current?.selected ?? []}
        onChange={setForCurrent}
        index={idx}
        total={total}
        reveal={result ? { correct: result.correct, correctIds: result.correctAnswerIds } : null}
      />

      {result && (
        <div className={`feedback ${result.correct ? 'feedback-ok' : 'feedback-wrong'}`}>
          <h3>
            {result.correct
              ? '🏅 Correct — nicely done!'
              : '❌ Not quite — see the highlighted answer above.'}
          </h3>
          {!result.correct && (
            <p>
              The detailed explanation will be shown on the results page at the end of
              the test.
            </p>
          )}
        </div>
      )}

      <nav className="attempt-nav">
        <button
          className="btn"
          disabled={idx === 0}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
        >
          ← Previous
        </button>
        {!result ? (
          <button
            className="btn btn-primary"
            disabled={!current?.selected?.length}
            onClick={submitAnswer}
          >
            Submit answer
          </button>
        ) : isLast ? (
          <button className="btn btn-primary" disabled={submitting} onClick={finishTest}>
            Finish test
          </button>
        ) : (
          <button
            className="btn btn-primary"
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
          >
            Next question →
          </button>
        )}
      </nav>

      <ol className="question-jump">
        {data.questions.map((qq, i) => {
          const a = answers[qq.id];
          const status = a?.result
            ? a.result.correct
              ? 'jump-correct'
              : 'jump-wrong'
            : a?.selected.length
              ? 'jump-answered'
              : '';
          return (
            <li key={qq.id}>
              <button
                className={`jump ${i === idx ? 'jump-current' : ''} ${status}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to question ${i + 1}`}
                title={
                  a?.result
                    ? a.result.correct
                      ? 'Correct'
                      : 'Wrong'
                    : a?.selected.length
                      ? 'Answered'
                      : 'Not yet answered'
                }
              >
                {a?.result?.correct ? '🏅' : i + 1}
              </button>
            </li>
          );
        })}
      </ol>

      <HelpPanel
        open={showHelp}
        onClose={() => setShowHelp(false)}
        currentOrder={idx + 1}
      />
    </div>
  );
}
